import { CatalogProductWithInventoryAndAttributes } from "@redotech/marketplace-sdk/rpc/schema/get-marketplace-products-by-vendor";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import { MarketplaceCartItem } from "@redotech/redo-marketplace-common/marketplace-cart";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { DataTable } from "@redotech/redo-web/components/ui/data-table";
import { Flex } from "@redotech/redo-web/flex";
import { ImageDisplayWithPlaceholder } from "@redotech/redo-web/image-display-with-placeholder";
import { Text } from "@redotech/redo-web/text";
import {
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { memo, useCallback, useMemo, useState } from "react";
import * as styles from "./bulk-purchase-table.module.css";

interface BulkPurchaseTableProps {
  products: CatalogProductWithInventoryAndAttributes[];
  onQuantitiesChange?: (cartItems: MarketplaceCartItem[]) => void;
}

interface BaseRowData {
  productId: string;
  productName: string;
  productImage: string;
}

type ProductRowData = BaseRowData & {
  type: "product";
  priceRange: string;
  totalAvailable: number;
  variantCount: number;
  defaultVariant: VariantRowData;
  subRows: VariantRowData[];
};

type VariantRowData = BaseRowData & {
  type: "variant";
  variantId: string;
  attribute1?: string;
  attribute2?: string;
  attribute3?: string;
  price: number;
  available: number;
  minQuantity?: number;
  quantity: number;
};

type TableRowData = ProductRowData | VariantRowData;

const columnHelper = createColumnHelper<TableRowData>();

export const BulkPurchaseTable = memo(function BulkPurchaseTable({
  products,
  onQuantitiesChange,
}: BulkPurchaseTableProps) {
  const [variantQuantities, setVariantQuantities] = useState<
    Map<string, number>
  >(() => {
    const initialQuantities = new Map<string, number>();
    for (const product of products) {
      for (const variant of product.variants) {
        const initialQuantity = variant.available;
        initialQuantities.set(variant._id, initialQuantity);
      }
    }
    return initialQuantities;
  });

  const handleQuantityChange = useCallback(
    (variantId: string, quantity: number) => {
      setVariantQuantities((prev) => {
        const newMap = new Map(prev);
        if (quantity <= 0) {
          newMap.delete(variantId);
        } else {
          newMap.set(variantId, quantity);
        }

        // Create cart items from the quantities map
        if (onQuantitiesChange) {
          const cartItems: MarketplaceCartItem[] = [];
          for (const [vid, qty] of newMap) {
            if (qty > 0) {
              // Find the product that contains this variant
              const product = products.find((p) =>
                p.variants.some((v) => v._id === vid),
              );
              if (product) {
                cartItems.push({
                  productId: product._id,
                  variantId: vid,
                  quantity: qty,
                });
              }
            }
          }
          onQuantitiesChange(cartItems);
        }

        return newMap;
      });
    },
    [onQuantitiesChange, products],
  );

  const getAttributeByValueId = useCallback(
    (
      product: CatalogProductWithInventoryAndAttributes,
      attributeValueId: string,
    ) => {
      for (const attribute of product.attributes) {
        const value = attribute.values.find(
          (v) => v._id.toString() === attributeValueId.toString(),
        );
        if (value) {
          return {
            attributeName: attribute.name,
            attributeValue:
              value.string ||
              value.boolean?.toString() ||
              value.decimal?.toString() ||
              value.integer?.toString() ||
              "",
          };
        }
      }
      return null;
    },
    [],
  );

  const tableData: ProductRowData[] = useMemo(() => {
    const rows: ProductRowData[] = [];

    for (const product of products) {
      const attributeNamesMap = new Map<string, number>();
      let attributeIndex = 0;

      for (const variant of product.variants) {
        for (const attributeValueId of variant.attributeValues) {
          const attrData = getAttributeByValueId(product, attributeValueId);
          if (attrData && !attributeNamesMap.has(attrData.attributeName)) {
            attributeNamesMap.set(attrData.attributeName, attributeIndex);
            attributeIndex++;
            if (attributeIndex >= 3) {
              break;
            }
          }
        }
        if (attributeIndex >= 3) {
          break;
        }
      }

      const attributeNames = Array.from(attributeNamesMap.entries())
        .sort((a, b) => a[1] - b[1])
        .map(([name]) => name);

      const variantRows: VariantRowData[] = product.variants.map((variant) => {
        const variantAttributeValues = new Map<string, string>();

        for (const attributeValueId of variant.attributeValues) {
          const attrData = getAttributeByValueId(product, attributeValueId);
          if (attrData) {
            variantAttributeValues.set(
              attrData.attributeName,
              attrData.attributeValue,
            );
          }
        }

        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const attribute1 = variantAttributeValues.get(attributeNames[0]);
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const attribute2 = variantAttributeValues.get(attributeNames[1]);
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const attribute3 = variantAttributeValues.get(attributeNames[2]);

        const attributeValues = [attribute1, attribute2, attribute3].filter(
          Boolean,
        );

        return {
          type: "variant",
          productId: product._id,
          productName: attributeValues.join(", "),
          productImage: product.images[0]?.url ?? "",
          variantId: variant._id,
          price: variant.price || 0,
          available: variant.available || 0,
          minQuantity: variant.minQuantity,
          quantity: variantQuantities.get(variant._id) || 0,
        };
      });

      // Calculate product-level summary data
      const prices = product.variants.map((v) => v.price || 0);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      const priceRange =
        minPrice === maxPrice
          ? `$${minPrice.toFixed(2)}`
          : `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
      const totalAvailable = product.variants.reduce(
        (sum, v) => sum + (v.available || 0),
        0,
      );

      const singleVariantProduct = product.variants.length === 1;

      rows.push({
        type: "product",
        productId: product._id,
        productName: product.name,
        productImage: product.images[0]?.url ?? "",
        priceRange,
        totalAvailable,
        variantCount: product.variants.length,
        subRows: singleVariantProduct ? [] : variantRows,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        defaultVariant: variantRows[0],
      });
    }

    return rows;
  }, [products, variantQuantities, getAttributeByValueId]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("productName", {
        header: "Product",
        cell: ({ row, getValue }) => {
          const isProduct = row.original.type === "product";
          const isVariant = row.original.type === "variant";
          const isExpanded = row.getIsExpanded();

          return (
            <Flex align="center" className="min-w-0" gap="sm">
              <div className="w-8 flex justify-center flex-shrink-0">
                {isProduct &&
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                  (row.original as ProductRowData).variantCount > 1 && (
                    <RedoButton
                      hierarchy="tertiary"
                      IconTrailing={
                        isExpanded ? ChevronDownIcon : ChevronRightIcon
                      }
                      onClick={() => row.toggleExpanded()}
                      size="sm"
                    />
                  )}
              </div>

              <div className="flex-shrink-0 w-12 h-12">
                {isProduct && (
                  <ImageDisplayWithPlaceholder
                    alt={getValue()}
                    className="w-full h-full object-cover rounded"
                    size="sm"
                    src={row.original.productImage}
                  />
                )}
              </div>

              {isProduct && (
                <div className="min-w-0 flex-1 pl-2">
                  <Text className="truncate" fontWeight="semibold">
                    {getValue()}
                  </Text>
                  {/* eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion */}
                  {(row.original as ProductRowData).variantCount > 1 ? (
                    <Text fontSize="xs" fontWeight="thin" textColor="secondary">
                      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion */}
                      {(row.original as ProductRowData).variantCount} variants
                    </Text>
                  ) : (
                    <Text fontSize="xs" fontWeight="thin" textColor="secondary">
                      {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                        (row.original as ProductRowData).defaultVariant
                          .productName
                      }
                    </Text>
                  )}
                </div>
              )}

              {isVariant && (
                <div className="min-w-0 flex-1">
                  <Text className="truncate" fontWeight="semibold">
                    {getValue()}
                  </Text>
                </div>
              )}
            </Flex>
          );
        },
      }),
      columnHelper.display({
        id: "price",
        header: () => <div className="text-left">Price</div>,
        cell: ({ row }) => {
          const isProduct = row.original.type === "product";
          return (
            <div className="text-left">
              <Text fontSize="sm" textColor="secondary">
                {isProduct
                  ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                    (row.original as ProductRowData).priceRange
                  : // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                    `$${(row.original as VariantRowData).price.toFixed(2)}`}
              </Text>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "available",
        header: () => <div className="text-right">Available</div>,
        cell: ({ row }) => {
          const isProduct = row.original.type === "product";
          return (
            <div className="text-right">
              <Text fontSize="sm" textColor="secondary">
                {isProduct
                  ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                    (row.original as ProductRowData).totalAvailable
                  : // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                    (row.original as VariantRowData).available}
              </Text>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "quantity",
        header: () => <div className="text-right">Quantity</div>,
        cell: ({ row }) => {
          const isProduct = row.original.type === "product";
          const singleVariantProduct =
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            isProduct && (row.original as ProductRowData).variantCount === 1;

          if (isProduct && !singleVariantProduct) {
            return null;
          }

          const variant = singleVariantProduct
            ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              (row.original as ProductRowData).defaultVariant
            : // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              (row.original as VariantRowData);

          return (
            <Flex align="center" className="justify-end" dir="row" gap="sm">
              {variant.minQuantity && variant.minQuantity > 0 ? (
                <Text
                  className="hidden sm:inline"
                  fontSize="sm"
                  textColor="tertiary"
                >
                  (min: {variant.minQuantity})
                </Text>
              ) : null}
              <div className="w-24">
                <RedoIncrementDecrement
                  fullWidth
                  max={variant.available}
                  min={variant.minQuantity || 0}
                  setValue={(value) => {
                    handleQuantityChange(variant.variantId, value);
                  }}
                  size="sm"
                  step={1}
                  value={variant.quantity || 0}
                />
              </div>
            </Flex>
          );
        },
      }),
      columnHelper.display({
        id: "total",
        header: () => <div className="text-right pr-4">Total</div>,
        cell: ({ row }) => {
          const isProduct = row.original.type === "product";
          const isVariant = row.original.type === "variant";

          let totalPrice = 0;

          if (isProduct) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const product = row.original as ProductRowData;

            if (product.subRows.length === 0) {
              totalPrice =
                product.defaultVariant.quantity * product.defaultVariant.price;
            } else {
              totalPrice = product.subRows.reduce(
                (sum, v) => sum + (v.quantity || 0) * (v.price || 0),
                0,
              );
            }
          }

          if (isVariant) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const variant = row.original as VariantRowData;
            totalPrice = variant.quantity * variant.price;
          }

          return (
            <div className="text-right pr-4">
              <Text fontWeight={isProduct ? "regular" : "semibold"}>
                {`$${totalPrice.toFixed(2)}`}
              </Text>
            </div>
          );
        },
      }),
    ],
    [handleQuantityChange],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => {
      if (row.type === "product") {
        return row.subRows;
      }
      return undefined;
    },
    meta: {},
  });

  return (
    <DataTable
      className={styles.bulkPurchaseTable}
      getRowProps={(row) => ({ "data-row-type": row.original.type })}
      table={table}
    />
  );
});
